import {
  AnagraficaDelegatoDTO,
  getGetFrontAnagraficheDelegatiQueryKey,
  usePostFrontAnagraficheDelegati,
  usePutFrontAnagraficheDelegatiId,
} from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import { FormHandler } from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { DrawerButtonRow } from '@framework/ui';
import { Button, Space, message } from 'antd';
import { useMemo, useState } from 'react';
import { queryClient } from '../../../core/network/queryClient';
import { AddressBookRecordInnerForm } from './AddressBookRecordInnerForm';
import {
  AddressBookRecordSchema,
  AddressBookUserOrigin,
  mapAddressBookToData,
} from './AddressBookRecordSchema';

export interface IAddressBookRecordFormProps {
  record: AnagraficaDelegatoDTO | null | undefined;
  onClose: () => void;
}

export function AddressBookRecordForm(props: IAddressBookRecordFormProps) {
  const { record, onClose } = props;

  const { t } = useTranslation();

  const isEdit = !!record?.id;

  const editMutation = usePutFrontAnagraficheDelegatiId();
  const createMutation = usePostFrontAnagraficheDelegati();

  const [loading, setLoading] = useState(false);

  const initialValues = useMemo(() => {
    if (!record) return null;
    if (!isEdit) return AddressBookRecordSchema.cast({}, { assert: false });

    const userOrigin = record.flUtenteEstero ? AddressBookUserOrigin.EXTRA : AddressBookUserOrigin.EU; // prettier-ignore

    return AddressBookRecordSchema.cast(
      { ...record, userOrigin },
      { assert: false },
    );
  }, [isEdit, record]);

  if (!initialValues) {
    return null;
  }

  return (
    <FormHandler
      initialValues={initialValues}
      onSubmit={async values => {
        try {
          setLoading(true);
          if (isEdit) {
            await editMutation.mutateAsync({
              id: record?.id ?? '<unknown>',
              data: mapAddressBookToData(values),
            });
            message.success(
              t(
                'address-book.edit.success',
                'Anagrafica delegato modificata con successo',
              ),
            );
          } else {
            await createMutation.mutateAsync({
              data: mapAddressBookToData(values),
            });
            message.success(
              t(
                'addres-book.create.success',
                'Anagrafica delegato creata con successo',
              ),
            );
          }
          await queryClient.invalidateQueries(getGetFrontAnagraficheDelegatiQueryKey()); // prettier-ignore
          setLoading(false);
          onClose();
        } catch (e) {
          console.error(e);
          message.error(
            getNetworkErrorMessage(
              e,
              t(
                'address-book.edit-create.error',
                'Errore durante il salvataggio',
              ),
            ),
          );
        } finally {
          setLoading(false);
        }
      }}
      schema={AddressBookRecordSchema}
    >
      <AddressBookRecordInnerForm />

      <DrawerButtonRow>
        <Space>
          <Button size="large" onClick={onClose} loading={loading}>
            {t('addess-book.form.cancel', 'Annulla')}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={loading}
          >
            {t('addess-book.form.apply', 'Applica')}
          </Button>
        </Space>
      </DrawerButtonRow>
    </FormHandler>
  );
}
